import type { PluggableList } from 'unified';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema, type Options as RehypeSanitizeOptions } from 'rehype-sanitize';

type SanitizeAttributes = NonNullable<RehypeSanitizeOptions['attributes']>;
type AttributeList = SanitizeAttributes[string];

/** Typical utility/Tailwind class strings seen in rendered markdown HTML. */
const SAFE_CLASS_NAME = /^[\w\s\-:/[\]().,#%]+$/;

/**
 * Semantic HTML tags that are safe to render but omitted from rehype-sanitize's
 * GitHub default schema.
 */
const ADDITIONAL_SAFE_HTML_TAG_NAMES = [
  'abbr',
  'address',
  'article',
  'aside',
  'bdi',
  'bdo',
  'caption',
  'cite',
  'col',
  'colgroup',
  'data',
  'dfn',
  'figcaption',
  'figure',
  'footer',
  'header',
  'main',
  'mark',
  'nav',
  'small',
  'time',
  'wbr',
] as const;

const HEADING_TAGS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const;

/** Explicitly blocked tags — executable, external-resource, or form controls. */
export const UNSAFE_HTML_TAG_NAMES = [
  'applet',
  'audio',
  'base',
  'button',
  'canvas',
  'embed',
  'form',
  'frame',
  'frameset',
  'iframe',
  'link',
  'meta',
  'object',
  'param',
  'script',
  'select',
  'slot',
  'style',
  'svg',
  'template',
  'textarea',
  'title',
  'video',
] as const;

const GLOBAL_ACCESSIBILITY_ATTRIBUTES: AttributeList = [
  'role',
  'ariaHidden',
  'ariaLabel',
  'ariaLabelledBy',
  'ariaDescribedBy',
  'ariaLive',
  'ariaBusy',
  'ariaCurrent',
];

function withAttributes(tag: string, extra: AttributeList = []): AttributeList {
  const base = defaultSchema.attributes?.[tag];
  return [...(base ?? []), ...extra];
}

const globalAttributes = withAttributes('*', [
  ['className', SAFE_CLASS_NAME],
  ...GLOBAL_ACCESSIBILITY_ATTRIBUTES,
]);

const headingAttributes = Object.fromEntries(
  HEADING_TAGS.map((tag) => [
    tag,
    withAttributes(tag, [['className', SAFE_CLASS_NAME], ['className', 'sr-only']]),
  ]),
) satisfies SanitizeAttributes;

/**
 * GitHub-style HTML allowlist extended with common semantic tags and class/ARIA
 * attributes needed by AI-generated markdown. Dangerous tags (script, iframe,
 * form controls, etc.) remain blocked via omission from `tagNames`.
 */
export const markdownSanitizeSchema: RehypeSanitizeOptions = {
  ...defaultSchema,
  tagNames: Array.from(
    new Set([...(defaultSchema.tagNames ?? []), ...ADDITIONAL_SAFE_HTML_TAG_NAMES]),
  ).filter((tag) => !(UNSAFE_HTML_TAG_NAMES as readonly string[]).includes(tag)),
  attributes: {
    ...defaultSchema.attributes,
    ...headingAttributes,
    '*': globalAttributes,
    a: withAttributes('a', [
      'target',
      'rel',
      ['className', SAFE_CLASS_NAME],
    ]),
    abbr: withAttributes('abbr', ['title', ['className', SAFE_CLASS_NAME]]),
    blockquote: withAttributes('blockquote', [['className', SAFE_CLASS_NAME]]),
    code: withAttributes('code', [['className', SAFE_CLASS_NAME]]),
    data: withAttributes('data', ['value', ['className', SAFE_CLASS_NAME]]),
    div: withAttributes('div', [['className', SAFE_CLASS_NAME]]),
    img: withAttributes('img', [
      'src',
      'alt',
      'title',
      ['className', SAFE_CLASS_NAME],
    ]),
    li: withAttributes('li', [['className', SAFE_CLASS_NAME]]),
    mark: [['className', SAFE_CLASS_NAME]],
    ol: withAttributes('ol', [['className', SAFE_CLASS_NAME]]),
    p: withAttributes('p', [['className', SAFE_CLASS_NAME]]),
    pre: withAttributes('pre', [['className', SAFE_CLASS_NAME]]),
    section: withAttributes('section', [['className', SAFE_CLASS_NAME]]),
    span: withAttributes('span', [['className', SAFE_CLASS_NAME], ['style', /^color:\s*[\w#(),.\s%]+;?\s*$/i]]),
    sub: withAttributes('sub', [['className', SAFE_CLASS_NAME]]),
    // Allow citation-badge inline styles so pasted/saved <sup> chips keep the grey pill.
    sup: withAttributes('sup', [
      ['className', SAFE_CLASS_NAME],
      [
        'style',
        /^(?:(?:background-color|color|font-size|line-height|border-radius|margin-left|min-width|min-height|padding-left|padding-right|display|vertical-align|text-align|white-space)\s*:\s*[^;]+;?\s*)+$/i,
      ],
    ]),
    table: withAttributes('table', [['className', SAFE_CLASS_NAME]]),
    td: withAttributes('td', [['className', SAFE_CLASS_NAME]]),
    th: withAttributes('th', [['className', SAFE_CLASS_NAME]]),
    time: withAttributes('time', ['dateTime', ['className', SAFE_CLASS_NAME]]),
    tr: withAttributes('tr', [['className', SAFE_CLASS_NAME]]),
    ul: withAttributes('ul', [['className', SAFE_CLASS_NAME]]),
  },
};

/** Sanitize rendered HTML nodes (safe default for markdown without raw HTML). */
export const markdownRehypeSanitizePlugins: PluggableList = [
  [rehypeSanitize, markdownSanitizeSchema],
];

/** Parse embedded HTML, then sanitize — use wherever legacy content includes inline HTML. */
export const markdownRehypeRawSanitizePlugins: PluggableList = [
  rehypeRaw,
  [rehypeSanitize, markdownSanitizeSchema],
];
